import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(() => window.scrollY > 60);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Shopping Guide", path: "/shopping-guide" },
    { label: "Reviews", path: "/#reviews" },
    { label: "FAQ", path: "/#faq" },
  ];

  const mobileCategories = [
    { name: "Shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html" },
    { name: "Hoodies & Sweaters", url: "https://kaspreadsheet.com/search-912/name/Shoes.html" },
    { name: "T-Shirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html" },
    { name: "Jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html" },
    { name: "Pants & Shorts", url: "https://kaspreadsheet.com/search-915/name/Shoes.html" },
    { name: "Headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html" },
    { name: "Sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html" },
    { name: "Jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html" },
    { name: "Accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html" },
    { name: "Bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html" },
    { name: "Perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html" },
    { name: "Other Stuff", url: "https://kaspreadsheet.com/search-924/name/Shoes.html" },
  ];

  const textColor = scrolled ? "text-foreground-900" : "text-white";
  const textHover = scrolled ? "hover:text-primary-500" : "hover:text-white/80";
  const logoColor = scrolled ? "text-foreground-900" : "text-white";
  const borderColor = scrolled ? "border-background-200/70" : "border-white/15";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 ${logoColor} hover:opacity-80 transition-opacity duration-200`}
          >
            <span className="text-xl font-heading font-semibold whitespace-nowrap">
              BaseTao Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.path.startsWith("/#") ? (
                <a
                  key={link.label}
                  href={link.path}
                  className={`text-sm font-medium ${textColor} ${textHover} transition-colors duration-200 whitespace-nowrap`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === link.path
                      ? scrolled ? "text-primary-500" : "text-white font-semibold"
                      : `${textColor} ${textHover}`
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Visit Store
            <i className="ri-arrow-right-up-line ml-1.5 w-4 h-4 flex items-center justify-center"></i>
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden border-t ${borderColor} px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto ${scrolled ? "bg-background-50" : "bg-black/70 backdrop-blur-md"}`}>
          <div className="flex flex-col gap-3">
            {navLinks.map((link) =>
              link.path.startsWith("/#") ? (
                <a
                  key={link.label}
                  href={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-sm font-medium py-2 whitespace-nowrap ${scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"} transition-colors duration-200`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-sm font-medium py-2 whitespace-nowrap transition-colors duration-200 ${
                    location.pathname === link.path
                      ? scrolled ? "text-primary-500" : "text-white font-semibold"
                      : scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}

            <div className={`border-t ${borderColor} pt-3 mt-1`}>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className={`w-full flex items-center justify-between py-2 text-sm font-medium whitespace-nowrap cursor-pointer ${scrolled ? "text-foreground-700" : "text-white/90"}`}
              >
                <span>Product Categories</span>
                <i className={`text-base w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1.5 mt-2 mb-2">
                  {mobileCategories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className={`text-xs py-2 px-2 rounded-lg whitespace-nowrap cursor-pointer transition-colors duration-150 ${scrolled ? "text-foreground-600 hover:text-primary-500 hover:bg-background-100" : "text-white/70 hover:text-white hover:bg-white/10"}`}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Visit Store
              <i className="ri-arrow-right-up-line ml-1.5 w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}